import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const StoreContext = createContext();

const DEFAULT_THEME = {
    primary_color: "#1a365d",
    secondary_color: "#2c5282",
    accent_color: "#d69e2e",
    background_color: "#ffffff",
    header_bg_color: "#1a365d",
    text_color: "#1a202c",
    font_family: "Cormorant Garamond, serif"
};

import { usePlatformContext } from './PlatformContext';

export const StoreProvider = ({ children, subdomain }) => {
    const { platformInfo } = usePlatformContext();
    const [store, setStore] = useState(null);
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [storeAppearance, setStoreAppearance] = useState({});
    const [shippingRates, setShippingRates] = useState({
        shipping_enabled: true,
        free_shipping_threshold: 500,
        shipping_tiers: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [collections, setCollections] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const fetchStoreData = async () => {
            if (!subdomain) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                
                // 1. Sanitize subdomain 
                let cleanSubdomain = decodeURIComponent(subdomain);
                if (cleanSubdomain.includes('/store/')) {
                    cleanSubdomain = cleanSubdomain.split('/store/')[1].split('/')[0].split('?')[0];
                } else if (cleanSubdomain.startsWith('http')) {
                    cleanSubdomain = cleanSubdomain.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('.')[0];
                }

                // 2. Fetch remaining data in parallel
                const promises = [
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/theme-presets`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/store-appearance`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/shipping-rates`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preview/${cleanSubdomain}`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/themes/default`)
                ];

                const [
                    themePresetsRes,
                    appearanceRes,
                    shippingRes,
                    previewRes,
                    defaultThemeRes
                ] = await Promise.all(promises);
                
                const themePresets = themePresetsRes.data;
                const appData = appearanceRes.data;
                const shipRates = shippingRes.data;
                const previewData = previewRes.data;
                const platformDefaultTheme = defaultThemeRes.data;
                const platformDefaultColors = platformDefaultTheme?.colors || {};

                setStoreAppearance(appData);
                setShippingRates(shipRates);
                setCollections(previewData.collections || []);
                setProducts(previewData.products || []);
                setCategories(previewData.categories || []);

                const storeData = previewData.store;
                setStore(storeData);

                // Resolve Theme - Priority logic:
                // 1. Reseller Custom Colors (storeData.theme.custom_X)
                // 2. Chosen Preset Colors (themePresets[presetName])
                // 3. Platform-wide Default Theme (platformDefaultTheme)
                // 4. Platform Appearance Settings (appData.X)
                // 5. System Hardcoded Fallback (DEFAULT_THEME)
                
                const presetName = storeData?.theme?.preset || "classic_elegant";
                const activePreset = themePresets[presetName] || {};

                setTheme({
                    primary_color: storeData?.theme?.custom_primary_color || activePreset.primary_color || platformDefaultColors.primary_color || appData.primary_color || DEFAULT_THEME.primary_color,
                    secondary_color: storeData?.theme?.custom_secondary_color || activePreset.secondary_color || platformDefaultColors.secondary_color || DEFAULT_THEME.secondary_color,
                    accent_color: storeData?.theme?.custom_accent_color || activePreset.accent_color || platformDefaultColors.accent_color || DEFAULT_THEME.accent_color,
                    background_color: storeData?.theme?.custom_background_color || activePreset.background_color || platformDefaultColors.background_color || DEFAULT_THEME.background_color,
                    header_bg_color: storeData?.theme?.custom_header_bg_color || activePreset.header_bg_color || platformDefaultColors.header_bg_color || DEFAULT_THEME.header_bg_color,
                    text_color: storeData?.theme?.custom_text_color || activePreset.text_color || platformDefaultColors.text_color || DEFAULT_THEME.text_color,
                    font_family: storeData?.theme?.custom_font_family || activePreset.font_family || platformDefaultTheme?.font_family || appData.font || DEFAULT_THEME.font_family
                });

            } catch (err) {
                console.error(`[DEBUG] StoreContext error for ${subdomain}:`, err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchStoreData();
    }, [subdomain]);

    return (
        <StoreContext.Provider value={{
            store,
            theme,
            storeAppearance,
            platformInfo,
            shippingRates,
            loading,
            error,
            collections,
            products,
            categories,
            subdomain,
            platformInfo
        }}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStoreContext = () => {
    const context = useContext(StoreContext);
    if (!context) {
        throw new Error("useStoreContext must be used within a StoreProvider");
    }
    return context;
};
